// src/features/patient/care-treatment/utils/sanitizeNoteHtml.ts

/**
 * Purpose : The only way a note's HTML reaches the page. Hospital notes are
 * untrusted markup, so this keeps text formatting and drops everything that
 * can run, load or submit: no script/style/iframe/object/embed/form/input, no
 * on* handlers, no javascript: or other non-web links, no remote images.
 * Links that survive open in a new tab with rel="noopener noreferrer".
 *
 * Contents :
 *
 * * function(sanitizeNoteHtml) : untrusted HTML → safe HTML string
 */

import DOMPurify from "dompurify";

const FORBID_TAGS = ["script", "style", "iframe", "object", "embed", "form", "input", "button", "select", "textarea", "link", "meta", "base", "svg", "math"];

// Only plain web and mail links; javascript:, data:, vbscript: etc. are dropped.
const SAFE_LINK = /^(?:https?:|mailto:)/i;

let purifier: ReturnType<typeof DOMPurify> | null = null;

/** One configured instance, hooks added once (not on the shared global). */
function getPurifier(): ReturnType<typeof DOMPurify> {
  if (purifier) return purifier;
  purifier = DOMPurify(window);
  purifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      const href = node.getAttribute("href") ?? "";
      if (!SAFE_LINK.test(href.trim())) {
        node.removeAttribute("href");
      } else {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
    // A note never needs a remote image: it would load from a third party on open.
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src") ?? "";
      if (!/^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(src.trim())) node.remove();
    }
  });
  return purifier;
}

// function(sanitizeNoteHtml)
/**
 * Clean a note's HTML for display.
 *
 * @param html - Markup as the source sent it.
 * @returns Safe HTML, or "" for empty input.
 */
export function sanitizeNoteHtml(html: string): string {
  if (!html) return "";
  return getPurifier().sanitize(html, {
    FORBID_TAGS,
    FORBID_ATTR: ["style", "srcset", "formaction", "action", "background", "poster"],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ["target"],
  });
}
